import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { deleteDestination, getDestinations } from '../../../../../api/endpoints/cmsContent';
import type { Destination } from '../../../../../api/types/cmsContent';
import { ApiErrorAlert } from '../../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import { ListSearchField } from '../../../../../components/ListSearchField';
import { DestinationFormDialog } from '../DestinationFormDialog';

/** List rendered as a plain MUI Table (@mui/x-data-grid is banned under this project's
 *  pinned TypeScript 4.9.5). Confirmed via local-backend swagger + full live curl round-trip
 *  (see api/types/cmsContent.ts header). This page is only ever rendered as a tab inside
 *  `SiteDetailPage` for one specific site, so `siteId` comes in as a fixed prop. There is no
 *  `GetDestinationById` route (confirmed) — the edit dialog is handed the row straight from
 *  this page's own query cache. */
export default function DestinationListPage({ siteId }: { siteId: number }) {
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Destination | null>(null);
  const [deleting, setDeleting] = useState<Destination | null>(null);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const query = useQuery({
    queryKey: ['cms', 'content', 'destinations', siteId],
    queryFn: () => getDestinations(siteId),
  });
  const rows: Destination[] = useMemo(() => query.data?.destinations ?? [], [query.data]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr');
    if (!term) return rows;
    return rows.filter((row) =>
      [row.title, row.summary, row.link]
        .filter((v) => v !== undefined && v !== null && v !== '')
        .join(' ')
        .toLocaleLowerCase('tr')
        .includes(term)
    );
  }, [rows, search]);

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteDestination(id),
    onSuccess: () => {
      enqueueSnackbar('Destinasyon silindi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['cms', 'content', 'destinations'] });
      setDeleting(null);
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Destinasyonlar</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Yeni Destinasyon
        </Button>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <ListSearchField value={search} onChange={setSearch} placeholder="Başlık, özet, link..." />
      </Stack>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={24} sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Başlık</TableCell>
              <TableCell>Dil</TableCell>
              <TableCell>Link</TableCell>
              <TableCell>Detay Sayısı</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    {rows.length === 0 ? 'Kayıtlı destinasyon bulunamadı.' : 'Aramayla eşleşen destinasyon bulunamadı.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {filteredRows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.title ?? '—'}</TableCell>
                <TableCell>{row.language ? <Chip size="small" label={row.language} /> : '—'}</TableCell>
                <TableCell>{row.link ?? '—'}</TableCell>
                <TableCell>{row.destinationDetails?.length ?? 0}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(row)} aria-label="Düzenle">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(row)} aria-label="Sil">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <DestinationFormDialog open={createOpen} destination={null} siteId={siteId} onClose={() => setCreateOpen(false)} />
      <DestinationFormDialog open={!!editing} destination={editing} siteId={siteId} onClose={() => setEditing(null)} />

      <ConfirmDialog
        open={!!deleting}
        title="Destinasyonu sil"
        description={`"${deleting?.title ?? ''}" destinasyonunu silmek istediğinize emin misiniz?`}
        loading={deleteMutation.isPending}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.id)}
        onCancel={() => setDeleting(null)}
      />
    </Box>
  );
}
